import {
  maxPermissionScope,
  PermissionScope,
  permissionKeySchema,
  permissionScopeSchema,
  type PermissionKeyEnum,
  type PermissionScopeEnum,
} from "@fitness-app/data-models";

export interface TeamMemberPermissionView {
  key: PermissionKeyEnum;
  description: string;
  overridable: boolean;
  /** Scopes the backend actually evaluates this permission at. */
  grantableScopes: PermissionScopeEnum[];
  baselineScope: PermissionScopeEnum;
  grantScope: PermissionScopeEnum | null;
  effectiveScope: PermissionScopeEnum;
}

export interface TeamMemberPermissionConfiguration {
  permissions: TeamMemberPermissionView[];
}

interface PermissionCatalogRow {
  key: string;
  description: string;
  overridable: boolean;
  grantableScopes?: string[] | null;
}

const allScopes: PermissionScopeEnum[] = [PermissionScope.ASSIGNED, PermissionScope.TEAM, PermissionScope.ALL];

const parseGrantableScopes = (value: string[] | null | undefined): PermissionScopeEnum[] => {
  if (!value?.length) {
    return allScopes;
  }

  const parsed = value
    .map((scope) => permissionScopeSchema.safeParse(scope))
    .flatMap((result) => (result.success ? [result.data] : []))
    .filter((scope) => scope !== PermissionScope.NONE);

  return parsed.length ? parsed : allScopes;
};

interface PermissionScopeRow {
  key: string;
  scope: string;
}

interface BuildPermissionConfigurationInput {
  catalog: PermissionCatalogRow[];
  baselines: PermissionScopeRow[];
  grants: PermissionScopeRow[];
}

const parseScopeRows = (rows: PermissionScopeRow[]): Map<PermissionKeyEnum, PermissionScopeEnum> => {
  const scopes = new Map<PermissionKeyEnum, PermissionScopeEnum>();

  for (const row of rows) {
    const key = permissionKeySchema.safeParse(row.key);
    const scope = permissionScopeSchema.safeParse(row.scope);

    if (key.success && scope.success) {
      scopes.set(key.data, scope.data);
    }
  }

  return scopes;
};

export const buildTeamMemberPermissionConfiguration = ({
  catalog,
  baselines,
  grants,
}: BuildPermissionConfigurationInput): TeamMemberPermissionConfiguration => {
  const baselineByKey = parseScopeRows(baselines);
  const grantByKey = parseScopeRows(grants);
  const permissions: TeamMemberPermissionView[] = [];

  for (const permission of catalog) {
    const key = permissionKeySchema.safeParse(permission.key);

    if (!key.success || typeof permission.description !== "string" || typeof permission.overridable !== "boolean") {
      continue;
    }

    const baselineScope = baselineByKey.get(key.data) ?? PermissionScope.NONE;
    const grantScope = permission.overridable ? (grantByKey.get(key.data) ?? null) : null;

    permissions.push({
      key: key.data,
      description: permission.description,
      overridable: permission.overridable,
      grantableScopes: parseGrantableScopes(permission.grantableScopes),
      baselineScope,
      grantScope,
      effectiveScope: grantScope ? maxPermissionScope(baselineScope, grantScope) : baselineScope,
    });
  }

  return { permissions };
};

const permissionScopes = [
  PermissionScope.NONE,
  PermissionScope.ASSIGNED,
  PermissionScope.TEAM,
  PermissionScope.ALL,
] as const;

/**
 * A grant may only raise the baseline, and only to a scope the backend reads.
 * Without the second filter the dialog offered scopes that were stored and then
 * silently ignored — e.g. products.resources.read is never evaluated at TEAM.
 */
export const getGrantablePermissionScopes = (
  baselineScope: PermissionScopeEnum,
  grantableScopes: PermissionScopeEnum[] = allScopes,
): PermissionScopeEnum[] => {
  return permissionScopes.filter(
    (scope) =>
      scope !== PermissionScope.NONE &&
      scope !== baselineScope &&
      maxPermissionScope(baselineScope, scope) === scope &&
      grantableScopes.includes(scope),
  );
};
